import { format } from 'date-fns';
import type { AccrualFrequency, Policy, PolicyForm, PolicyRule, PolicySection } from '../types/hr';
import { TODAY } from './format';

const UNIT: Record<AccrualFrequency, string> = {
  Monthly: 'month',
  'Per pay period': 'pay period',
  Annually: 'year',
};

function days(n: string): string {
  return Number(n) === 1 ? 'day' : 'days';
}

/** Plain-English, one-paragraph description of a policy's rules. Blank values read as TBD. */
export function describePolicy(form: PolicyForm): { sentence: string; tbd: string[] } {
  const tbd: string[] = [];
  if (form.type === 'Unlimited') {
    return { sentence: 'Unlimited time off — requests are tracked for coverage, with no balance or accrual.', tbd };
  }

  const wait = Number(form.waitingDays) || 0;
  let accrual: string;
  if (form.accrualRate) {
    const verb = form.type === 'Fixed grant' ? 'Grants' : 'Accrues';
    accrual = `${verb} ${form.accrualRate} ${days(form.accrualRate)}/${UNIT[form.accrualFrequency]}${wait ? ` after ${wait} days` : ' from day one'}${form.prorate ? ', prorated' : ''}`;
  } else {
    tbd.push('Accrual rate');
    accrual = `Accrual rate TBD${wait ? ` (starts after ${wait} days)` : ''}`;
  }

  let cap: string;
  if (form.yearOneCap) {
    cap = `capped at ${form.yearOneCap} in year one`;
  } else {
    tbd.push('Cap');
    cap = 'cap TBD';
  }

  let anniversary: string;
  if (form.anniversaryAmount) {
    anniversary =
      form.anniversaryMode === 'top-up'
        ? `topped up to ${form.anniversaryAmount} at anniversary`
        : `+${form.anniversaryAmount} ${days(form.anniversaryAmount)} added at anniversary`;
  } else {
    tbd.push('Anniversary rule');
    anniversary = 'anniversary rule TBD';
  }

  let carryover: string;
  if (form.carryover) {
    carryover = `Carries over up to ${form.carryover} days.`;
  } else {
    tbd.push('Carryover');
    carryover = 'Carryover TBD.';
  }

  return { sentence: `${accrual}, ${cap}; ${anniversary}. ${carryover}`, tbd };
}

function rule(label: string, value: string): PolicyRule {
  return value ? { label, value } : { label, value: 'TBD', tbd: true };
}

/** Rebuild the read-only summary (highlights + sections) from the editor form. */
export function buildPolicy(base: Policy | undefined, form: PolicyForm, id: string): Policy {
  const unit = UNIT[form.accrualFrequency];
  const wait = Number(form.waitingDays) || 0;
  const amount = Number(form.anniversaryAmount) || 0;
  const unlimited = form.type === 'Unlimited';
  const { sentence, tbd } = describePolicy(form);
  const hasTbd = tbd.length > 0;

  const accrualValue = form.accrualRate ? `${form.accrualRate} ${days(form.accrualRate)} per ${unit}` : '';
  const anniversaryValue = amount
    ? form.anniversaryMode === 'top-up'
      ? `Top balance UP TO ${amount} days (not +${amount})`
      : `Add ${amount} days to the balance`
    : '';

  const sections: PolicySection[] = [
    {
      id: 'eligibility',
      title: 'Eligibility & waiting period',
      rules: [
        { label: 'Eligible departments', value: form.departments.length ? form.departments.join(', ') : 'None selected' },
        { label: 'Waiting period', value: wait ? `${wait} calendar days from hire date` : 'None' },
      ],
    },
    {
      id: 'accrual',
      title: 'Accrual',
      rules: unlimited
        ? [{ label: 'Accrual', value: 'Not applicable — unlimited policy' }]
        : [
            rule('Accrual rate', accrualValue),
            { label: 'Frequency', value: form.accrualFrequency },
            { label: 'Proration', value: form.prorate ? 'Yes — first partial period prorated' : 'No' },
          ],
    },
    { id: 'caps', title: 'Caps', rules: [rule('Year-one maximum', form.yearOneCap ? `${form.yearOneCap} days` : '')] },
    { id: 'anniversary', title: 'Anniversary rule', rules: [rule('Rule', anniversaryValue)] },
    { id: 'carryover', title: 'Carryover', rules: [rule('Carryover limit', form.carryover ? `${form.carryover} days` : '')] },
  ];

  const highlights: PolicyRule[] = [
    rule('Accrual', form.accrualRate ? `${form.accrualRate} ${days(form.accrualRate)} / ${unit}` : ''),
    rule('Year-one cap', form.yearOneCap ? `${form.yearOneCap} days` : ''),
    rule('Anniversary', amount ? (form.anniversaryMode === 'top-up' ? `Top up to ${amount}` : `Add ${amount}`) : ''),
  ];

  const keepOverview = base && base.hasTbd === hasTbd;

  return {
    id,
    name: form.name.trim(),
    tagline: base?.tagline ?? `Custom ${form.type.toLowerCase()} policy.`,
    type: form.type,
    updated: format(TODAY, 'yyyy-MM-dd'),
    hasTbd,
    overview: keepOverview && base ? base.overview : sentence,
    highlights,
    sections,
    example:
      form.anniversaryMode === 'top-up' && amount > 10
        ? {
            before: '10 days banked',
            added: `+${amount - 10} days`,
            after: `${amount} days`,
            caption: `The top-up brings the balance to ${amount} — it never adds a flat ${amount}. An employee with ${amount} or more receives nothing.`,
          }
        : undefined,
    form: { ...form, departments: [...form.departments] },
  };
}
